/**
 * OperationsReportPanel — live-filtered billing operations report on shared report shell.
 */

import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react'
import {
  fetchOperationsReport,
  exportOperationsReport,
  type OperationsQuery,
} from '../../../api/operationsReport'
import DashboardDrillDownPanel, { type DrillDownView } from '../../dashboard/DashboardDrillDownPanel'
import {
  OpsKpiSection,
  OpsAgingChartSection,
  OpsMonthlyChartSection,
  OpsOpenPayAppsSection,
  OpsCalloutSection,
} from './ReportShellSections'
import OpsReportSkeleton from './OpsReportSkeleton'
import { opsReportBodyMode } from '../../reports/opsReportView.js'
import './BooksReportsPanel.css'
import './OperationsReportPanel.css'

const PERIODS = [
  { id: 'month', label: 'This month' },
  { id: 'quarter', label: 'This quarter' },
  { id: 'ytd', label: 'YTD' },
  { id: 'custom', label: 'Custom' },
] as const

export default function OperationsReportPanel() {
  const [searchParams] = useSearchParams()
  const [period, setPeriod] = useState('month')
  const [projectId, setProjectId] = useState(
    () => searchParams.get('project') || searchParams.get('project_id') || '',
  )
  const [owner, setOwner] = useState('')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [exportBusy, setExportBusy] = useState<'csv' | 'pdf' | null>(null)
  const [exportErr, setExportErr] = useState<string | null>(null)
  const [drillDown, setDrillDown] = useState<DrillDownView | null>(null)

  useEffect(() => {
    const fromUrl = searchParams.get('project') || searchParams.get('project_id')
    if (fromUrl) setProjectId(fromUrl)
  }, [searchParams])

  const queryParams: OperationsQuery = useMemo(() => ({
    period,
    projectId: projectId || undefined,
    owner: owner || undefined,
    startDate: period === 'custom' ? customStart : undefined,
    endDate: period === 'custom' ? customEnd : undefined,
  }), [period, projectId, owner, customStart, customEnd])

  const reportQ = useQuery({
    queryKey: ['operations-report', queryParams],
    queryFn: () => fetchOperationsReport(queryParams),
    enabled: period !== 'custom' || (!!customStart && !!customEnd),
    placeholderData: (prev) => prev,
  })

  const data = reportQ.data
  const selectedProjectName = (data?.filterOptions ?? []).find((p) => String(p.id) === projectId)?.name
    || data?.filters?.projectName
    || ''
  const owners = useMemo(() => {
    const set = new Set<string>()
    for (const p of data?.filterOptions ?? []) {
      if (p.owner_name) set.add(p.owner_name)
    }
    return [...set].sort()
  }, [data?.filterOptions])

  async function handleExport(format: 'csv' | 'pdf') {
    setExportBusy(format)
    setExportErr(null)
    try {
      const { blob, filename } = await exportOperationsReport(format, queryParams)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      setExportErr('Export failed')
    } finally {
      setExportBusy(null)
    }
  }

  return (
    <div className="ops-reports books-reports" data-testid="operations-report-panel">
      <div className="books-toolbar">
        <label className="fld-l">Period</label>
        <select className="fld-i books-select" value={period}
          onChange={e => setPeriod(e.target.value)} data-testid="ops-period-select">
          {PERIODS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <label className="fld-l">Project</label>
        <select className="fld-i books-select" value={projectId}
          onChange={e => setProjectId(e.target.value)} data-testid="ops-filter-project">
          <option value="">All projects</option>
          {projectId
            && !(data?.filterOptions ?? []).some(p => String(p.id) === projectId)
            && (
              <option value={projectId}>{selectedProjectName || 'Selected project'}</option>
            )}
          {(data?.filterOptions ?? []).map(p => (
            <option key={p.id} value={String(p.id)}>{p.name}</option>
          ))}
        </select>
        <label className="fld-l">Owner</label>
        <select className="fld-i books-select" value={owner}
          onChange={e => setOwner(e.target.value)} data-testid="ops-filter-owner">
          <option value="">All owners</option>
          {owners.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {period === 'custom' && (
          <div className="books-custom-range">
            <input type="date" className="fld-i" value={customStart} onChange={e => setCustomStart(e.target.value)} data-testid="ops-custom-start" />
            <span>–</span>
            <input type="date" className="fld-i" value={customEnd} onChange={e => setCustomEnd(e.target.value)} data-testid="ops-custom-end" />
          </div>
        )}
        <div className="books-export">
          <button type="button" className="btn b-ol btn-sm" disabled={!!exportBusy || !data}
            onClick={() => handleExport('csv')} data-testid="ops-export-csv">
            {exportBusy === 'csv' ? <Loader2 size={14} className="spin" aria-hidden /> : <FileSpreadsheet size={14} />}
            Export CSV
          </button>
          <button type="button" className="btn b-ol btn-sm" disabled={!!exportBusy || !data}
            onClick={() => handleExport('pdf')} data-testid="ops-export-pdf">
            {exportBusy === 'pdf' ? <Loader2 size={14} className="spin" aria-hidden /> : <Download size={14} />}
            Export PDF
          </button>
        </div>
        {exportErr && <p className="books-export-err">{exportErr}</p>}
        {data?.filters && (
          <p className="books-export-hint" data-testid="ops-report-header">
            {selectedProjectName ? `${selectedProjectName} · ` : ''}
            {data.filters.label} · generated {new Date(data.generatedAt).toLocaleString()}
          </p>
        )}
      </div>

      {opsReportBodyMode({ isPending: reportQ.isFetching, hasData: !!data }) === 'skeleton' && (
        <OpsReportSkeleton />
      )}
      {reportQ.error && <p style={{ fontSize: 12, color: 'var(--red)' }}>Could not load report.</p>}

      {opsReportBodyMode({ isPending: reportQ.isFetching, hasData: !!data }) === 'data' && data && (
        <div className="rpt-shell-body">
          <OpsKpiSection data={data} onDrillDown={setDrillDown} />
          <OpsAgingChartSection data={data} />
          <OpsMonthlyChartSection data={data} />
          <OpsOpenPayAppsSection data={data} />
          <OpsCalloutSection data={data} />
        </div>
      )}
      <DashboardDrillDownPanel view={drillDown} onClose={() => setDrillDown(null)} />
    </div>
  )
}
